import { ChronoField, ChronoUnit, DateTimeFormatter, Duration, Instant, LocalDate, LocalDateTime, LocalTime, ZonedDateTime, ZoneId } from "@js-joda/core";
import { concatWords, pluralize } from "./English";
import { Locale } from "@js-joda/locale_en-us";
import { format } from "date-fns";

const browserDateFormat     = DateTimeFormatter.ofPattern( "yyyy-MM-dd" );
const browserTimeFormat     = DateTimeFormatter.ofPattern( "HH:mm"      );
const dateFormat            = DateTimeFormatter.ofPattern( "MM/dd/yyyy" );
const timeFormat            = DateTimeFormatter.ofPattern( "h:mm"       );
const shortTimeFormat       = DateTimeFormatter.ofPattern( "h"          );

export const splitDuration = ( d: Duration ) => {
    const h  = d.toHours();
    const m  = d.minusHours( h ).toMinutes();
    const s  = d.minusMinutes( m ).seconds();
    const ms = d.minusSeconds( s ).toMillis();
    return [h, m, s, ms];
}

export function showDuration( d: Duration ) {
    const [h, m] = splitDuration( d );

    //word pluralization
    const hWord = `${pluralize( h, "hour",   "hours"   )}`;
    const mWord = `${pluralize( m, "minute", "minutes" )}`;

    //string handling
    const hs = h > 0 ? `${h} ${hWord}` : "";
    const ms = m > 0 ? `${m} ${mWord}` : "";
    return concatWords( hs, ms );
}

export function showDurationShort( d: Duration ) {
    const [h, m] = splitDuration( d );

    //string handling
    const hs = h > 0 ? `${h}h` : "";
    const ms = m > 0 ? `${m}m` : "";
    if( hs === "" && ms === "" ) {
        return "0m";
    }
    return concatWords( hs, ms );
}

export function showTime( t: LocalTime ) {
    const am   = t.isBefore( LocalTime.of( 12, 0, 0, 0 ) );
    const amPm = am ? "am" : "pm";
    return `${t.format( timeFormat )} ${amPm}`;
}

export function showTimeShort( t: LocalTime ) {
    const am   = t.isBefore( LocalTime.of( 12, 0, 0, 0 ) );
    const amPm = am ? "am" : "pm";
    if( t.minute() === 0 ) {
        return `${t.format( shortTimeFormat )} ${amPm}`;
    }
    return `${t.format( timeFormat )} ${amPm}`;
}

export function showBrowserTime( d: LocalTime ) {
    return browserTimeFormat.format( d );
}

export function showBrowserDate( d: LocalDate ) {
    return browserDateFormat.format( d );
}

export function showDate( d: LocalDate ) {
    const today    = LocalDate.now();
    const tomorrow = today.plusDays( 1 );
    if( d.equals( today    ) ) { return "Today";    }
    if( d.equals( tomorrow ) ) { return "Tomorrow"; }
    return dateFormat.format( d );
}

export function showDateTime( d: LocalDateTime ) {
    const today = LocalDate.now();
    const isToday = d.toLocalDate().equals( today );
    const words: string[] = [];
    words.push( isToday ? "Today" : dateFormat.format( d ) );
    words.push( showTime( d.toLocalTime() ) );
    return concatWords( ...words );
}

export function showStartEndShort( a: LocalDateTime, b: LocalDateTime ) {
    const [ald, bld] = [a, b].map( dt => dt.toLocalDate() );
    const [ats, bts] = [a, b].map( dt => showTimeShort( dt.toLocalTime() ) );
    const [ads, bds] = [a, b].map( dt => showDate( dt.toLocalDate() ) );
    if( ald.equals( bld ) ) {
        //pattern: da from ta to tb
        return `${ads} from ${ats} to ${bts}`;
    }
    //pattern: da ta to db tb
    return `${ads} from ${ats} to ${bds} at ${bts}`;
}

export function showStartEnd( a: LocalDateTime, b: LocalDateTime ) {
    const [ald, bld] = [a, b].map( dt => dt.toLocalDate() );
    const [ats, bts] = [a, b].map( dt => showTime( dt.toLocalTime() ) );
    const [ads, bds] = [a, b].map( dt => showDate( dt.toLocalDate() ) );
    if( ald.equals( bld ) ) {
        //pattern: da from ta to tb
        return `${ads} from ${ats} to ${bts}`;
    }
    //pattern: da ta to db tb
    return `${ads} from ${ats} to ${bds} at ${bts}`;
}

export function showStartEndLocalTime( a: LocalTime, b: LocalTime ) {
    const [ats, bts] = [a, b].map( lt => showTime( lt ) );
    return `${ats} - ${bts}`;
}

export function showStartEndLocalTimeShort( a: LocalTime, b: LocalTime ) {
    const [ats, bts] = [a, b].map( lt => showTimeShort( lt ) );
    return `${ats} - ${bts}`;
}

export function showInBetweenOutBetween( a: LocalTime, b: LocalTime, c: LocalTime, d: LocalTime ): { inRange: string, outRange: string } {
    const [ats, bts, cts, dts] = [a, b, c, d].map( lt => showTime( lt ) );
    return { inRange: `${ats} - ${bts}`, outRange: `${cts} - ${dts}` };
}

export function getStartOfDay( dt: LocalDateTime ) {
    return dt.toLocalDate().atTime( LocalTime.MIN )
}

//I think the first day of the week
export function getStartOfWeek( dt: LocalDateTime ) {
    return dt.with( ChronoField.DAY_OF_WEEK, 1 ).truncatedTo( ChronoUnit.DAYS );
}

export function getStartOfMonth( dt: LocalDateTime ) {
    return dt.withDayOfMonth( 1 ).truncatedTo( ChronoUnit.DAYS );
}

function getFirstOfYear( dateTime: LocalDateTime ) {
    return dateTime.withDayOfYear( 1 ).truncatedTo( ChronoUnit.DAYS );
}

export function createLocalTime( date: string ) {
    const localDate = LocalTime.parse( date );
    return localDate;
}

export function createLocalDate( date: string ) {
    const localDate = LocalDate.parse( date );
    return localDate;
}

export function createLocalDateTime( date: string, time: string ) {
    const localDate     = LocalDate.parse(  date       );
    const localTime     = LocalTime.parse(  time       );
    const localDateTime = LocalDateTime.of( localDate, localTime );
    return localDateTime;
}

export function tryCreateLocalTime( time: string ): LocalTime | null {
    try {
        return LocalTime.parse( time );
    }
    catch( error ) {
        return null;
    }
}

export function tryCreateLocalDate( date: string ): LocalDate | null {
    try {
        return LocalDate.parse( date );
    }
    catch( error ) {
        return null;
    }
}

export function tryCreateLocalDateTime( date: string, time: string ): LocalDateTime | null {
    try {
        const localDate = LocalDate.parse( date );
        const localTime = LocalTime.parse( time );
        return LocalDateTime.of( localDate, localTime );
    }
    catch( error ) {
        return null;
    }
}

export const DateTimeFmt = {
    pad: {
        ms:   DateTimeFormatter.ofPattern( "MM/dd/uuuu hh:mm:ss.SSS a" ).withLocale( Locale.US ),
        sec:  DateTimeFormatter.ofPattern( "MM/dd/uuuu hh:mm:ss a"     ).withLocale( Locale.US ),
        min:  DateTimeFormatter.ofPattern( "MM/dd/uuuu hh:mm a"        ).withLocale( Locale.US ),
        hour: DateTimeFormatter.ofPattern( "MM/dd/uuuu hh a"           ).withLocale( Locale.US ),
        day:  DateTimeFormatter.ofPattern( "MM/dd/uuuu"                ).withLocale( Locale.US ),
        mon:  DateTimeFormatter.ofPattern( "MM/uuuu"                   ).withLocale( Locale.US ),
        year: DateTimeFormatter.ofPattern( "uuuu"                      ).withLocale( Locale.US ),
    },
    ms:       DateTimeFormatter.ofPattern( "MM/dd/uuuu h:mm:ss.SSS a" ).withLocale( ( Locale.US ) ),
    sec:      DateTimeFormatter.ofPattern( "MM/dd/uuuu h:mm:ss a"     ).withLocale( ( Locale.US ) ),
    min:      DateTimeFormatter.ofPattern( "MM/dd/uuuu h:mm a"        ).withLocale( ( Locale.US ) ),
    hour:     DateTimeFormatter.ofPattern( "MM/dd/uuuu h a"           ).withLocale( ( Locale.US ) ),
    day:      DateTimeFormatter.ofPattern( "MM/dd/uuuu"               ).withLocale( ( Locale.US ) ),
    mon:      DateTimeFormatter.ofPattern( "MM/uuuu"                  ).withLocale( ( Locale.US ) ),
    year:     DateTimeFormatter.ofPattern( "uuuu"                     ).withLocale( ( Locale.US ) ),
}
export const toZonedDateTime          = ( inst: Instant, zoneId: ZoneId = ZoneId.systemDefault() ) => inst.atZone( zoneId );
export const showInstantDateTimePadMs = ( inst: Instant, zone: string ) => toZonedDateTime( inst, ZoneId.of( zone ) ).format( DateTimeFmt.pad.ms );
export const showInstantDateTimeMs    = ( inst: Instant, zone: string ) => toZonedDateTime( inst, ZoneId.of( zone ) ).format( DateTimeFmt.ms     );
export const showInstantDateTimeSec   = ( inst: Instant, zone: string ) => toZonedDateTime( inst, ZoneId.of( zone ) ).format( DateTimeFmt.sec    );
export const showInstantDateTime      = ( inst: Instant, zone: string ) => toZonedDateTime( inst, ZoneId.of( zone ) ).format( DateTimeFmt.min    );
export const showInstantDate          = ( inst: Instant, zone: string ) => toZonedDateTime( inst, ZoneId.of( zone ) ).format( DateTimeFmt.day    );
export const showInstantLocalDate     = ( inst: Instant               ) => toZonedDateTime( inst                    ).format( DateTimeFmt.day    );
export const showInstantLocalDateTime = ( inst: Instant               ) => toZonedDateTime( inst                    ).format( DateTimeFmt.min    );

export function encodeLocalDate( date: LocalDate ): string {
    return date.format( DateTimeFormatter.ISO_LOCAL_DATE )
}

export function encodeLocalTime( time: LocalTime ): string {
    return time.format( DateTimeFormatter.ISO_LOCAL_TIME )
}

export function encodeLocalDateTime( dateTime: LocalDateTime ): string {
    return dateTime.format( DateTimeFormatter.ISO_LOCAL_DATE_TIME )
}
export function toLocalDate(dateTime: string): string {
    const _dateTime = new Date(Date.parse(dateTime));
    return LocalDate.of(_dateTime.getFullYear(), _dateTime.getMonth() + 1, _dateTime.getDate())
                    .format(DateTimeFormatter.ISO_LOCAL_DATE);
}

export function toLocalDateTime(dateTime: string): string {
  if (!dateTime) return "";

  const d = new Date(dateTime);

  const options: Intl.DateTimeFormatOptions = {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };

  return d.toLocaleString("en-US", options);
}

export function formatUtcTimeToLocal12h(timeUtc: string): string {
    const [utcHourStr, utcMinuteStr] = timeUtc.split(":");
    const utcDate = new Date();

    utcDate.setUTCHours(
      parseInt(utcHourStr, 10),
      parseInt(utcMinuteStr ?? "0", 10),
      0,
      0
    );

    return format(utcDate, "h a");
  }

export function convert12hToUtc(time12h: string): string {
  const [hourStr, period] = time12h.split(" ");
  let hour = parseInt(hourStr, 10) % 12;
  if (period === "PM") hour += 12;

  const localTime = LocalTime.of(hour, 0);
  const zoned = ZonedDateTime.now(ZoneId.systemDefault()).with(localTime);
  const utc = zoned.withZoneSameInstant(ZoneId.of("UTC")).toLocalTime();
  return encodeLocalTime(utc); 
}

export function stringToDateTime(linkExpiry: string): string {
    const zonedUtc = ZonedDateTime.parse(linkExpiry);
    const localDateTime = zonedUtc
      .withZoneSameInstant(ZoneId.SYSTEM)
      .toLocalDateTime();
    return showDateTime(localDateTime);
  }

export function localDateToInstantUtc(date: string): string {
  const localDate = LocalDate.parse(date);

  return localDate
    .atStartOfDay(ZoneId.of("UTC"))
    .toInstant()
    .toString();
}

export function toIsoLocalDate(d: any): string {
  if (typeof d === "string") return d;

  return `${d.year.toString().padStart(4, "0")}-${d.month
    .toString()
    .padStart(2, "0")}-${d.day.toString().padStart(2, "0")}`;
}

export function formatIsoDateShort(isoDate: string | null | undefined): string {
  if (!isoDate) return "—";
  const [datePart] = String(isoDate).split("T");
  const [y, m, d] = datePart.split("-");
  return `${parseInt(m)}/${parseInt(d)}/${y}`;
}
